import { encode } from 'html-entities';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import {
  COMPOSER_MAX_CHARACTER_LENGTH,
  COMPOSER_RATE_LIMIT_BLOCK_TIME_MS,
  EMOJI_LIST
} from '../../../constants';
import { CHAT_MESSAGE_EVENT_TYPES } from '../../../constants';
import { channel as $channelContent } from '../../../content';
import { CHAT_USER_ROLE, SEND_ERRORS } from './useChatConnection/utils';
import { clsm } from '../../../utils';
import { EmojiReactionLight, EmojiReactionDark } from '../../../assets/icons';
import { useChannel } from '../../../contexts/Channel';
import { useResponsiveDevice } from '../../../contexts/ResponsiveDevice';
import { useUser } from '../../../contexts/User';
import ComposerErrorMessage from './ComposerErrorMessage';
import Input from '../../../components/Input';
import { useTheme } from '../../../contexts/Theme';
import { RECORD_EMOJI_CLICK } from '../../../graphql/operations';
import Spinner from '../../../components/Spinner';
import { generateClient } from '@aws-amplify/api';
import useClickAway from '../../../hooks/useClickAway';
import { useChat } from '../../../contexts/Chat';

const client = generateClient();

const { SEND_MESSAGE } = CHAT_MESSAGE_EVENT_TYPES;

const $content = $channelContent.chat;

const Composer = ({
  chatUserRole = undefined,
  isDisabled = false,
  isFocusable = true,
  isLoading = true,
  sendAttemptError = null,
  sendMessage,
  isRequestButtonVisible = false,
  onLocalEmojiTriggered = () => {} //
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const composerFieldRef = useRef();
  const emojiQueueRef = useRef([]);
  const emojiPickerRef = useRef();
  const emojiButtonRef = useRef();
  const { isDark } = useTheme();
  const { channelData } = useChannel();
  const { isViewerBanned: isLocked } = channelData || {};
  const { isLandscape } = useResponsiveDevice();
  const { isSessionValid, userData } = useUser();
  const { isOnline } = useChat();
  const [message, setMessage] = useState('');
  const [showEmojis, setShowEmojis] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [shouldShake, setShouldShake] = useState(false); // Composer has shake animated only on submit
  const [blockChat, setBlockChat] = useState(false);
  const isUserAllowToMessage =
    userData?.userRole === 'Admin' || userData?.userRole === 'VIPuser';
  // const currentPage = useCurrentPage();
  // const isStreamManagerPage = currentPage === 'stream_manager';
  const canSendMessages =
    chatUserRole &&
    [CHAT_USER_ROLE.SENDER, CHAT_USER_ROLE.MODERATOR].includes(chatUserRole);
  const focus = location.state?.focus;

  const setSubmitErrorStates = (_errorMessage) => {
    setErrorMessage(`${$content.error.message_not_sent} ${_errorMessage}`);
    setShouldShake(true);
  };

  const navigateToLogin = () =>
    navigate('/login', { state: { from: location, focus: 'COMPOSER' } });

  const handleOnChange = (event) => {
    // If the user isn't logged in, redirect them to the login page
    if ((!isLoading && canSendMessages === false) || !isSessionValid) {
      navigateToLogin();
    }

    const { value } = event.target;
    const encodedValue = encode(value);
    // This is done to ensure we get the correct message length as it seems the IVS Chat API trims the message before checking its length
    const trimmedValue = encodedValue.trim();

    setMessage(value);

    // On change errors
    if (trimmedValue.length > COMPOSER_MAX_CHARACTER_LENGTH) {
      setErrorMessage($content.error.max_length_reached);
    } else if (!blockChat) {
      setErrorMessage('');
    }
  };

  const handleSendMessage = (event) => {
    event.preventDefault();
    if (isDisabled) return;

    if (!isOnline) {
      setSubmitErrorStates($content.notifications.info.network_offline);
      return;
    }

    if (isLoading) {
      setSubmitErrorStates($content.error.wait_until_connected);
    } else {
      if (canSendMessages) {
        if (!message || blockChat) return;
        if (errorMessage.includes($content.error.wait_until_connected)) {
          setErrorMessage('');
          setMessage('');
        }

        sendMessage(message, {
          eventType: SEND_MESSAGE,
          displayName: `${userData?.given_name} ${userData?.family_name}`,
          userId: userData?.sub
        });
        !errorMessage && setMessage('');
        setShouldShake(false);
      } else {
        if (canSendMessages === undefined) {
          setSubmitErrorStates($content.error.wait_until_connected);
        } else {
          navigateToLogin();
        }
      }
    }
  };

  const processEmojiQueue = useCallback(() => {
    const queue = emojiQueueRef.current;
    if (queue.length > 0) {
      const uniqueEmojiTypes = [
        ...new Set(queue.map((emoji) => emoji.emojiType))
      ].filter(Boolean);

      if (uniqueEmojiTypes.length > 0) {
        client.graphql({
          query: RECORD_EMOJI_CLICK,
          variables: { emoji: uniqueEmojiTypes }
        });
      }

      emojiQueueRef.current = [];
    }
  }, []);

  // Create stable debounced function - delay will be set on each emoji click
  const debouncedProcessEmojis = useMemo(
    () => (delay) => debounce(processEmojiQueue, delay),
    [processEmojiQueue]
  );

  const handleEmojiClick = (emoji) => {
    if (emoji.emojiType) {
      // Calculate new random delay for each click
      const randomDelay = Math.random() * 2000 + 1000; // Random delay between 500ms (1s) and 3000ms (3s)
      emojiQueueRef.current.push(emoji);
      // Trigger local animation immediately for visual feedback
      onLocalEmojiTriggered?.(emoji);
      // Trigger debounced processing
      debouncedProcessEmojis(randomDelay)();
    }
  };

  useEffect(() => {
    if (isOnline) {
      // If the user is online, reset the error message
      setErrorMessage('');
    }
  }, [isOnline]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (
        debouncedProcessEmojis &&
        typeof debouncedProcessEmojis.cancel === 'function'
      ) {
        debouncedProcessEmojis.cancel();
      }
    };
  }, [debouncedProcessEmojis]);

  useEffect(() => {
    // If previous route has focus state, focus on composer
    if (focus && focus === 'COMPOSER') {
      composerFieldRef.current.focus();
    }
  }, [focus]);

  useEffect(() => {
    // If user is banned, remove any message
    if (isLocked) setMessage('');
  }, [isLocked]);

  useEffect(() => {
    // If there is a connection error, clear the current error message
    if (isDisabled) setErrorMessage('');
  }, [isDisabled]);

  useEffect(() => {
    if (blockChat) {
      const blockChatTimerId = setTimeout(() => {
        setBlockChat(false);
        setErrorMessage('');
      }, COMPOSER_RATE_LIMIT_BLOCK_TIME_MS);

      return () => clearTimeout(blockChatTimerId);
    }
  }, [blockChat]);

  useEffect(() => {
    // Send errors
    if (sendAttemptError) {
      let sendAttemptErrorMessage = '';

      if (sendAttemptError.message === SEND_ERRORS.RATE_LIMIT_EXCEEDED) {
        setBlockChat(true);
        sendAttemptErrorMessage = $content.error.rate_exceeded;
      } else if (sendAttemptError.message === SEND_ERRORS.MAX_LENGTH_EXCEEDED) {
        sendAttemptErrorMessage = $content.error.max_length_reached;
      }

      // connection error or chat is loading (chat is not connected)
      setSubmitErrorStates(sendAttemptErrorMessage);
    }
  }, [sendAttemptError, isLoading]);

  // Close emoji picker when clicking outside
  useClickAway([emojiPickerRef, emojiButtonRef], () => {
    setShowEmojis(false);
  });

  if (!userData?.userRole || isLoading) {
    return (
      <div className="w-full flex items-center justify-center">
        <Spinner className="dark:text-white" />
      </div>
    );
  }
  return (
    <div className="w-full flex items-center justify-center gap-2">
      {isUserAllowToMessage && (
        <motion.div
          animate={shouldShake ? 'shake' : 'default'}
          variants={{
            shake: { x: [12, -12, 8, -8, 4, 0] },
            default: { x: 0 }
          }}
          transition={{ duration: 0.5 }}
          className="flex-1 w-full"
        >
          <form
            className={clsm(
              'relative',
              isSessionValid && [
                isRequestButtonVisible
                  ? 'w-full'
                  : [
                      'md:w-[calc(100%)]',
                      isLandscape &&
                        'touch-screen-device:lg:w-[calc(100%_-_60px)]'
                    ]
              ]
            )}
            onSubmit={handleSendMessage}
          >
            <div>
              <ComposerErrorMessage errorMessage={errorMessage} />
              <Input
                {...(!isFocusable ? { tabIndex: -1 } : {})}
                ariaLabel={isDisabled ? 'Chat disabled' : null}
                autoComplete="off"
                className={clsm(
                  [
                    'w-full',
                    'bg-lightMode-gray',
                    'dark:bg-darkMode-gray',
                    'dark:focus:bg-darkMode-gray-medium',
                    'dark:hover:bg-darkMode-gray-medium',
                    'dark:placeholder-darkMode-gray-light',
                    'dark:text-white',
                    'focus:bg-lightMode-gray',
                    'hover:bg-lightMode-gray',
                    'placeholder-lightMode-gray-dark',
                    'read-only:hover:bg-lightMode-gray',
                    'read-only:hover:placeholder-lightMode-gray-dark',
                    'read-only:hover:placeholder-lightMode-gray-medium'
                  ],
                  isDisabled && ['opacity-30']
                )}
                error={errorMessage ? '' : null}
                isRequired={false}
                name="chatComposer"
                onChange={handleOnChange}
                placeholder={
                  !isOnline
                    ? 'Chat unavailable - You are offline'
                    : isLocked
                      ? $content.you_are_banned
                      : $content.say_something
                }
                readOnly={isDisabled || isLocked || !isOnline}
                ref={composerFieldRef}
                value={message}
              />
            </div>
          </form>
        </motion.div>
      )}
      {isUserAllowToMessage && (
        <div className={clsm('relative', isUserAllowToMessage && 'ml-auto')}>
          <button
            type="button"
            disabled={!isOnline}
            onClick={() => setShowEmojis(!showEmojis)}
            className={`h-11 w-11 flex items-center justify-center rounded-full bg-gray-200 dark:bg-darkMode-gray-hover ${!isOnline && 'opacity-95 cursor-not-allowed'}`}
            ref={emojiButtonRef}
          >
            {isDark ? (
              <div className="w-7 h-7">
                <EmojiReactionDark className="w-full h-full" />
              </div>
            ) : (
              <div className="w-7 h-7">
                <EmojiReactionLight className="w-full h-full" />
              </div>
            )}
          </button>

          {showEmojis && (
            <div
              ref={emojiPickerRef}
              className="absolute bottom-full right-0 mb-2 bg-white/80 dark:bg-darkMode-gray backdrop-blur-sm p-2 rounded-full shadow-lg flex flex-col gap-2"
            >
              {EMOJI_LIST.map((emoji) => (
                <button
                  key={emoji.emojiType}
                  type="button"
                  className="hover:bg-gray-100/90 dark:hover:bg-darkMode-gray-hover p-1 rounded-full text-2xl w-10 h-10"
                  onClick={(e) => handleEmojiClick(emoji, e)}
                >
                  {emoji.emojiIcon}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {!isUserAllowToMessage && (
        <div className="mb-2 bg-white dark:bg-darkMode-gray p-2 rounded-full  shadow-lg flex gap-2">
          {EMOJI_LIST.map((emoji) => (
            <button
              key={emoji.emojiType}
              type="button"
              className="hover:bg-gray-100  dark:hover:bg-darkMode-gray-hover p-1 rounded-full text-2xl flex items-center justify-center"
              onClick={(e) => handleEmojiClick(emoji, e)}
            >
              {emoji.emojiIcon}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

Composer.propTypes = {
  chatUserRole: PropTypes.oneOf(Object.values(CHAT_USER_ROLE)),
  isDisabled: PropTypes.bool,
  isFocusable: PropTypes.bool,
  isLoading: PropTypes.bool,
  sendAttemptError: PropTypes.shape({ message: PropTypes.string }),
  sendMessage: PropTypes.func.isRequired,
  isRequestButtonVisible: PropTypes.bool,
  onLocalEmojiTriggered: PropTypes.func
};

export default Composer;
